import type { RankingElement } from '@empathybroker/tsclient-play/dist/models/ranking-element'
import type {
  Identifiable,
  QueryRankingConfigDTO,
  QueryRankingConfigWithIdDTO,
  QueryRankingElementRankingDTO,
  QueryRankingHiddenElementDTO,
} from '@empathyco/playboard-core-domain'
import type { Dictionary } from '../models'
import { DomainDateUtils, DomainObjectUtils } from '@empathyco/playboard-core-domain'

export class ConfigurationsUtils {
  static getRankingElementId(
    element: RankingElement | QueryRankingElementRankingDTO | QueryRankingHiddenElementDTO,
  ): string {
    const sortedFilterIds = Object.keys(element.filters).sort(
      (filterId1: string, filterId2: string) => filterId1.localeCompare(filterId2),
    )
    const filtersString = sortedFilterIds
      .reduce((result: string[], filterId: string) => {
        const sortedFilterValues =
          (element.filters[filterId] as Array<string>)?.sort(
            (filterValue1: string, filterValue2: string) =>
              filterValue1.localeCompare(filterValue2),
          ) ?? []
        if (sortedFilterValues.length) {
          result.push(`${filterId}=${sortedFilterValues.join('|')}`)
        }
        return result
      }, [])
      .join('&')
    const { activationDate, deactivationDate } = element.schedule
    const scheduleString = `${activationDate?.toISOString() ?? ''}-${
      deactivationDate?.toISOString() ?? ''
    }`
    return `${element.action}_${element.fieldName}=${element.fieldValue}_${filtersString}_${scheduleString}`
  }

  static removeQueryRankingElementById(
    queryRankingConfigs: Array<QueryRankingConfigDTO>,
    queryRankingConfigId: QueryRankingConfigWithIdDTO['id'],
  ): Array<QueryRankingConfigDTO> {
    return queryRankingConfigs.filter(
      queryRankingConfig =>
        queryRankingConfigId !==
        ConfigurationsUtils.getRankingElementId(
          queryRankingConfig as QueryRankingElementRankingDTO,
        ),
    )
  }

  static areQueryRankingConfigSchedulesOverlapped(
    schedule1: QueryRankingConfigDTO['schedule'],
    schedule2: QueryRankingConfigDTO['schedule'],
  ): boolean {
    return DomainDateUtils.arePeriodsOverlapped(
      {
        start: schedule1?.activationDate,
        end: schedule1?.deactivationDate,
      },
      {
        start: schedule2.activationDate,
        end: schedule2.deactivationDate,
      },
    )
  }

  static areFiltersOverlapped(
    filters1: Dictionary<string[]>,
    filters2: Dictionary<string[]>,
  ): boolean {
    return Object.entries(filters1).every(([filterId, filterValues1]: [string, string[]]) => {
      const filterValues2 = filters2[filterId] || []
      if (!filterValues2.length || !filterValues1.length) {
        return true
      }
      return filterValues1.some((value: string) => filterValues2.includes(value))
    })
  }

  static findQueryRankingConfigByFieldValue<T extends { fieldValue?: string }>(
    productId: string,
    elementRankings: Array<T>,
  ): T | undefined {
    return elementRankings.find(productRanking => productId === productRanking.fieldValue)
  }

  static updateQueryRankingConfig(
    queryRankingConfigs: Array<QueryRankingConfigDTO>,
    existingQueryRankingConfig: QueryRankingConfigDTO & Identifiable,
    newQueryRankingConfig: QueryRankingConfigDTO,
  ): Array<QueryRankingConfigDTO> {
    const queryRankingConfigsCopy = queryRankingConfigs.map(queryRankingConfig =>
      DomainObjectUtils.clone(queryRankingConfig),
    )
    const queryRankingConfigIndex = queryRankingConfigs.findIndex(
      queryRankingConfig =>
        existingQueryRankingConfig.id ===
        ConfigurationsUtils.getRankingElementId(
          queryRankingConfig as QueryRankingElementRankingDTO,
        ),
    )
    queryRankingConfigsCopy[queryRankingConfigIndex] = newQueryRankingConfig
    return queryRankingConfigsCopy
  }

  static addQueryRankingConfigs(
    queryRankingConfigs: Array<QueryRankingConfigDTO>,
    queryRankingConfig: QueryRankingConfigDTO | Array<QueryRankingConfigDTO>,
  ): Array<QueryRankingConfigDTO> {
    return [
      ...queryRankingConfigs,
      ...(Array.isArray(queryRankingConfig) ? queryRankingConfig : [queryRankingConfig]),
    ]
  }

  static filterArrayByFieldValue(
    queryRankingConfigs: Array<QueryRankingConfigDTO>,
    fieldValue: string,
  ): Array<QueryRankingConfigDTO> {
    return queryRankingConfigs.filter(
      queryRankingConfig => queryRankingConfig.fieldValue !== fieldValue,
    )
  }
}
